class HeaderManager {
  constructor(options = {}) {
    this._config = {
      headerRootSelector: '[data-header-root]',
      containerSelector: '[data-header]',
      triggerSelector: '[data-dropdown-trigger]',
      menuSelector: '[data-dropdown-menu]',
      menuToggleSelector: '[data-menu-toggle]',
      openDelay: 150,
      closeDelay: 200,
      openClass: 'is-open',
      activeClass: 'is-active',
      ...options,
    };

    this._timers = new Map();
    this._activeMenu = null;

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._handleDrawerOpen = this._handleDrawerOpen.bind(this);
    this._handleDrawerClose = this._handleDrawerClose.bind(this);

    this._init();
    this._bindGlobalEvents();
  }

  _init() {
    const container = document.querySelector(this._config.containerSelector);
    if (container) {
      container.querySelectorAll(this._config.triggerSelector).forEach((trigger) => {
        const name = trigger.dataset.dropdownTrigger;
        const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
        if (!menu) return;

        trigger.addEventListener('mouseenter', () => this._scheduleOpen(name));
        trigger.addEventListener('mouseleave', () => this._scheduleClose(name));

        const hoverTarget = menu.querySelector('[data-dropdown-content]') || menu;
        hoverTarget.addEventListener('mouseenter', () => this._cancelClose(name));
        hoverTarget.addEventListener('mouseleave', () => this._scheduleClose(name));

        trigger.addEventListener('focus', () => this._scheduleOpen(name));
        trigger.addEventListener('blur', () => this._scheduleClose(name));
      });
    }

    this._syncHeaderHeight();
  }

  _bindGlobalEvents() {
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click', this._onClick);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('load', this._onResize);

    window.eventBus?.on('drawer:open', this._handleDrawerOpen);
    window.eventBus?.on('drawer:close', this._handleDrawerClose);
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this.closeDropdowns();
  }

  _onClick(e) {
    if (!e.target.closest(this._config.triggerSelector) && !e.target.closest(this._config.menuSelector)) {
      this.closeDropdowns();
    }
  }

  _onResize() {
    this._syncHeaderHeight();
  }

  _handleDrawerOpen({ name }) {
    this.closeDropdowns();

    if (name === 'menu') {
      document.querySelectorAll(this._config.menuToggleSelector).forEach((button) => {
        button.classList.add(this._config.activeClass);
        button.setAttribute('aria-expanded', 'true');
      });
    }
  }

  _handleDrawerClose({ name }) {
    if (name === 'menu') {
      document.querySelectorAll(this._config.menuToggleSelector).forEach((button) => {
        button.classList.remove(this._config.activeClass);
        button.setAttribute('aria-expanded', 'false');
      });
    }
  }

  _syncHeaderHeight() {
    const roots = [...document.querySelectorAll(this._config.headerRootSelector)];
    if (!roots.length) return;

    const maxHeight = roots.reduce((height, root) => {
      return Math.max(height, Math.ceil(root.getBoundingClientRect().height));
    }, 0);

    if (maxHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${maxHeight}px`);
    }
  }

  _scheduleOpen(name) {
    this._cancelClose(name);
    const timer = setTimeout(() => this._open(name), this._config.openDelay);
    this._timers.set(`open:${name}`, timer);
  }

  _scheduleClose(name) {
    this._cancelOpen(name);
    const timer = setTimeout(() => this._close(name), this._config.closeDelay);
    this._timers.set(`close:${name}`, timer);
  }

  _cancelOpen(name) {
    clearTimeout(this._timers.get(`open:${name}`));
    this._timers.delete(`open:${name}`);
  }

  _cancelClose(name) {
    clearTimeout(this._timers.get(`close:${name}`));
    this._timers.delete(`close:${name}`);
  }

  _open(name) {
    if (this._activeMenu && this._activeMenu !== name) {
      this._close(this._activeMenu);
    }

    const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.add(this._config.openClass);
    menu.removeAttribute('aria-hidden');
    trigger?.classList.add(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'true');
    this._activeMenu = name;

    window.eventBus?.emit('dropdown:open', { name, menu, trigger });
  }

  _close(name) {
    const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.remove(this._config.openClass);
    menu.setAttribute('aria-hidden', 'true');
    trigger?.classList.remove(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'false');

    if (this._activeMenu === name) this._activeMenu = null;

    window.eventBus?.emit('dropdown:close', { name, menu });
  }

  closeDropdowns() {
    if (this._activeMenu) this._close(this._activeMenu);
  }

  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click', this._onClick);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onResize);
    this._timers.forEach((timer) => clearTimeout(timer));
    this._timers.clear();
  }
}

window.HeaderManager = new HeaderManager();
